import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { apiClient } from '@/services/api'
import { useAuthStore } from '@/stores/auth'

// Demo workspaces are wiped server-side after a window of inactivity. We mirror
// that window client-side as a countdown that resets on user activity, surface
// it in the demo banner, and auto-exit when it elapses.
const ACTIVE_KEY = 'hudstore_demo_active'
const TIMEOUT_KEY = 'hudstore_demo_timeout_minutes'
const DEADLINE_KEY = 'hudstore_demo_deadline'

const DEFAULT_TIMEOUT_MINUTES = 60

export const useDemoStore = defineStore('demo', () => {
  const active = ref(false)
  const embedded = ref(false) // running inside the landing iframe (memory-only)
  const idleTimeoutMinutes = ref(DEFAULT_TIMEOUT_MINUTES)
  const deadline = ref<number | null>(null) // epoch ms when the demo auto-exits
  const now = ref(Date.now())

  let ticker: ReturnType<typeof setInterval> | null = null

  const msLeft = computed(() => {
    if (!active.value || deadline.value == null) return 0
    return Math.max(0, deadline.value - now.value)
  })
  const minutesLeft = computed(() => Math.ceil(msLeft.value / 60_000))
  const isExpired = computed(() => active.value && msLeft.value <= 0)

  const persist = () => {
    localStorage.setItem(ACTIVE_KEY, active.value ? '1' : '0')
    localStorage.setItem(TIMEOUT_KEY, String(idleTimeoutMinutes.value))
    if (deadline.value != null) localStorage.setItem(DEADLINE_KEY, String(deadline.value))
  }

  const clearPersisted = () => {
    localStorage.removeItem(ACTIVE_KEY)
    localStorage.removeItem(TIMEOUT_KEY)
    localStorage.removeItem(DEADLINE_KEY)
  }

  const resetActivity = () => {
    if (!active.value) return
    deadline.value = Date.now() + idleTimeoutMinutes.value * 60_000
    persist()
  }

  const startTicker = () => {
    if (ticker) return
    ticker = setInterval(() => {
      now.value = Date.now()
      if (isExpired.value) void endDemoSession()
    }, 1_000)
  }

  const stopTicker = () => {
    if (ticker) {
      clearInterval(ticker)
      ticker = null
    }
  }

  // Begin a demo: claim a workspace, auto-login, start the countdown.
  const startDemoSession = async () => {
    const response = await apiClient.startDemo()
    idleTimeoutMinutes.value = response.demo?.idle_timeout_minutes ?? DEFAULT_TIMEOUT_MINUTES
    active.value = true
    resetActivity()
    startTicker()
    return response
  }

  // Embedded (landing iframe) demo: memory-only so it never touches the parent
  // tab's session. Nothing is persisted; a reload starts fresh.
  const startEmbeddedDemo = async () => {
    const authStore = useAuthStore()
    authStore.setMemoryOnly(true)
    embedded.value = true
    const response = await apiClient.startDemo()
    idleTimeoutMinutes.value = response.demo?.idle_timeout_minutes ?? DEFAULT_TIMEOUT_MINUTES
    active.value = true
    deadline.value = Date.now() + idleTimeoutMinutes.value * 60_000
    startTicker()
    return response
  }

  const endDemoSession = async () => {
    stopTicker()
    active.value = false
    deadline.value = null
    clearPersisted()
    const authStore = useAuthStore()
    try {
      await apiClient.logout()
    } catch {
      authStore.logout()
    }
  }

  // Re-arm a persisted demo after a page reload, but only if the auth session
  // actually survived (refresh cookie + restored user). Otherwise clean up.
  const restore = () => {
    if (localStorage.getItem(ACTIVE_KEY) !== '1') return
    const authStore = useAuthStore()
    if (!authStore.hasActiveSession && !authStore.isAuthenticated) {
      clearPersisted()
      return
    }
    idleTimeoutMinutes.value = Number(localStorage.getItem(TIMEOUT_KEY)) || DEFAULT_TIMEOUT_MINUTES
    const savedDeadline = Number(localStorage.getItem(DEADLINE_KEY)) || 0
    active.value = true
    deadline.value = savedDeadline > Date.now() ? savedDeadline : Date.now() + idleTimeoutMinutes.value * 60_000
    startTicker()
  }

  return {
    active,
    embedded,
    idleTimeoutMinutes,
    minutesLeft,
    msLeft,
    isExpired,
    startDemoSession,
    startEmbeddedDemo,
    endDemoSession,
    resetActivity,
    restore
  }
})
